import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarGroup } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { getInitials, getColorClass } from "@/components/user-display"
import { TeamRoleBadge } from "@/components/role-badge"
import { Pencil, Trash2, Users } from "lucide-react"
import type { TeamMembershipRole } from "@/types"

type TeamMembership = {
  id: string
  role: TeamMembershipRole
  user: {
    id: string
    first_name: string
    last_name: string
    email_address: string
    is_active: boolean
  }
}

type TeamWithMembers = {
  id: string
  name: string
  organisation_id: string
  parent_team_id: string | null
  organisation?: { id: string; name: string }
  parent_team?: { id: string; name: string }
  sub_teams?: { id: string; name: string }[]
  team_memberships?: TeamMembership[]
}

type Props = {
  team: TeamWithMembers
  onEdit: (team: TeamWithMembers) => void
  onDelete?: (team: TeamWithMembers) => void
  onManageMembers?: (team: TeamWithMembers) => void
  canEdit: boolean
}

const ROLE_ORDER: Record<string, number> = { manager: 0, member: 1 }

function sortMemberships(memberships: TeamMembership[]): TeamMembership[] {
  return [...memberships].sort((a, b) => {
    if (a.user.is_active !== b.user.is_active) return a.user.is_active ? -1 : 1
    return (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)
  })
}

export function TeamCard({ team, onEdit, onDelete, onManageMembers, canEdit }: Props) {
  const memberships = sortMemberships(team.team_memberships || [])
  const displayMemberships = memberships.slice(0, 5)
  const remaining = memberships.length - displayMemberships.length

  return (
    <Card className="relative">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex flex-col gap-0.5">
          <CardTitle className="text-base">{team.name}</CardTitle>
          {(team.parent_team || (team.sub_teams && team.sub_teams.length > 0)) && (
            <p className="text-xs text-muted-foreground">
              {team.parent_team && <>Sous-équipe de {team.parent_team.name}</>}
              {team.parent_team && team.sub_teams && team.sub_teams.length > 0 && " · "}
              {team.sub_teams && team.sub_teams.length > 0 && (
                <>
                  {team.sub_teams.length} sous-équipe{team.sub_teams.length > 1 ? "s" : ""}
                </>
              )}
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1">
            {onManageMembers && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.preventDefault()
                      onManageMembers(team)
                    }}
                  >
                    <Users className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Gérer les membres</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={(e) => {
                    e.preventDefault()
                    onEdit(team)
                  }}
                >
                  <Pencil className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Modifier</TooltipContent>
            </Tooltip>
            {onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.preventDefault()
                      onDelete(team)
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Supprimer</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {memberships.length > 0 ? (
          <div className="flex flex-col gap-2">
            {displayMemberships.map((membership) => (
              <div
                key={membership.id}
                className={`flex items-center gap-3${!membership.user.is_active ? " opacity-50" : ""}`}
              >
                <Avatar size="sm">
                  <AvatarFallback
                    className={getColorClass(membership.user.first_name, membership.user.last_name)}
                  >
                    {getInitials(membership.user.first_name, membership.user.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium leading-tight">
                    {membership.user.first_name} {membership.user.last_name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground leading-tight">
                    {membership.user.email_address}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <TeamRoleBadge role={membership.role} />
                  <span
                    className={`inline-block size-2 rounded-full ${membership.user.is_active ? "bg-green-500" : "bg-red-500"}`}
                    title={membership.user.is_active ? "Actif" : "Inactif"}
                  />
                </div>
              </div>
            ))}
            {remaining > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 pt-1 cursor-pointer rounded-md px-1 -mx-1 hover:bg-muted transition-colors"
                  >
                    <AvatarGroup>
                      {memberships.slice(5).map((membership) => (
                        <Avatar key={membership.id} size="sm">
                          <AvatarFallback
                            className={getColorClass(membership.user.first_name, membership.user.last_name)}
                          >
                            {getInitials(membership.user.first_name, membership.user.last_name)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                    </AvatarGroup>
                    <span className="text-xs text-muted-foreground">+ {remaining} autres</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3">
                  <div className="flex flex-col gap-2">
                    {memberships.slice(5).map((membership) => (
                      <div
                        key={membership.id}
                        className={`flex items-center gap-3${!membership.user.is_active ? " opacity-50" : ""}`}
                      >
                        <Avatar size="sm">
                          <AvatarFallback
                            className={getColorClass(membership.user.first_name, membership.user.last_name)}
                          >
                            {getInitials(membership.user.first_name, membership.user.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium leading-tight">
                            {membership.user.first_name} {membership.user.last_name}
                          </span>
                          <span className="truncate text-xs text-muted-foreground leading-tight">
                            {membership.user.email_address}
                          </span>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <TeamRoleBadge role={membership.role} />
                          <span
                            className={`inline-block size-2 rounded-full ${membership.user.is_active ? "bg-green-500" : "bg-red-500"}`}
                            title={membership.user.is_active ? "Actif" : "Inactif"}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">Aucun membre</p>
        )}
      </CardContent>
    </Card>
  )
}

export type { TeamWithMembers, TeamMembership }
